import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Users, Globe2, Quote } from "lucide-react";

export default function About() {
  return (
    <div className="relative">
      {/* Promise hero */}
      <section className="relative pt-36 pb-24 overflow-hidden grain">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#1D2AE8]/80 font-semibold"
          >
            The Promise · Signed, Sealed, Delivered
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-display leading-[0.88] tracking-tight mt-6"
            style={{ fontSize: "clamp(3rem, 11vw, 10rem)" }}
          >
            Double your
            <br />
            <em className="not-italic text-[#1D2AE8]">digital growth</em>
            <br />
            in <span className="underline decoration-[6px] underline-offset-[8px] decoration-[#0B0B0B]">one year.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div className="glass-dark rounded-3xl p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                Guarantee
              </p>
              <p className="mt-3 text-white/90 leading-relaxed">
                2× measurable digital growth within 12 months, or we keep
                working — at our cost — until we hit it.
              </p>
            </div>
            <div className="glass-dark rounded-3xl p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                Measured By
              </p>
              <p className="mt-3 text-white/90 leading-relaxed">
                Revenue, qualified leads, subscribers, sell-through, or
                whichever north-star metric you sign onto.
              </p>
            </div>
            <div className="glass-dark rounded-3xl p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                Signed Into
              </p>
              <p className="mt-3 text-white/90 leading-relaxed">
                Every engagement. Kept for 106 brands. In writing, with
                milestones and quarterly reviews.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 bg-[#FFE0EC] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-5">
            <div className="glass-light rounded-[32px] p-10 sm:p-12 aspect-[4/5] flex flex-col justify-between relative overflow-hidden">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8] font-semibold">
                  Studio Founder
                </p>
                <p className="font-display text-5xl sm:text-6xl mt-4 leading-[0.9]">
                  Dr. I. C.
                </p>
                <p className="mt-3 text-sm text-[#0B0B0B]/70">
                  MBBS · Founder & Managing Director
                </p>
              </div>
              <div>
                <Quote size={32} className="text-[#FF3D6A]" />
                <p className="font-italic-serif text-2xl leading-tight mt-3 text-[#0B0B0B]">
                  "Taste and rigour, in equal measure."
                </p>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-[#FFC629] opacity-70 blur-2xl" />
              <div className="absolute -top-16 -left-10 w-40 h-40 rounded-full bg-[#1D2AE8] opacity-30 blur-2xl" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
              The Founder
            </p>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mt-3">
              Dr. Indranil
              <br />
              <em className="font-italic-serif text-[#FF3D6A]">Chatterjee</em>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#0B0B0B]/85 max-w-xl">
              A physician (MBBS) turned brand builder, Indranil founded
              Illustrative Digital in 2016 with a simple thesis: the internet
              rewards taste and rigour, in equal measure. Nine years on, the
              studio is ranked among the top 15 digital marketing firms in
              India — quietly shaping category-defining brands across
              streetwear, denim, footwear, healthcare and wellness.
            </p>
            <blockquote className="mt-8 border-l-4 border-[#1D2AE8] pl-6 font-display text-2xl italic text-[#0B0B0B] max-w-lg">
              "We don't chase trends. We give brands the language to make
              them."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-24">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#1D2AE8]/70 font-semibold">
            By the Numbers
          </p>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mt-3 max-w-3xl">
            A studio of twelve. A footprint of one hundred and six.
          </h2>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "2016", v: "Founded in Kolkata" },
              { k: "12", v: "Full-time studio team" },
              { k: "38", v: "Indian brand partners" },
              { k: "72", v: "Global brand partners" },
            ].map((s, i) => (
              <motion.div
                key={s.k}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-light rounded-3xl p-6"
              >
                <p className="font-display text-5xl">{s.k}</p>
                <p className="mt-2 text-sm text-[#1D2AE8]/70 leading-relaxed">
                  {s.v}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#0B0B0B] text-[#FFF6E0]">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FFC629] font-semibold">
            Studio Principles
          </p>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] mt-3 max-w-3xl">
            Rigour, taste, and one <em className="text-[#FFC629]">unbreakable</em> promise.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Award />,
                t: "Editorial-first",
                d: "Every deliverable held to a magazine-cover standard. Bad taste is bad business.",
              },
              {
                icon: <Users />,
                t: "Small, senior",
                d: "Twelve senior craftspeople — no interns running your brand, ever.",
              },
              {
                icon: <Globe2 />,
                t: "Globally native",
                d: "We work across timezones like locals. Berlin, Brighton, Bangalore all in one week.",
              },
            ].map((v) => (
              <div key={v.t} className="glass-dark rounded-3xl p-8">
                <div className="text-[#FFC629]">{v.icon}</div>
                <p className="font-display text-3xl mt-6">{v.t}</p>
                <p className="text-white/70 mt-3 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-white/10">
            <p className="font-display text-2xl max-w-lg">
              Curious if we're the right studio for your next chapter?
            </p>
            <Link
              to="/contact"
              className="bg-[#FFC629] text-[#0B0B0B] font-semibold rounded-full px-6 py-3 inline-flex items-center gap-2 hover:bg-[#FFE0EC]"
            >
              Say hello <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
